import { axiosInstance, type CustomResponseFormat } from "api/instance";
import {
  isTaskEditorState,
  normalizeTaskEditorState,
  normalizePublishedTaskEditorTask,
  type PublishedTaskEditorTask,
} from "pages/task-editor/taskEditorRuntime";
import type { TaskEditorState } from "pages/task-editor/taskEditorUtils";

const TASK_EDITOR_DOMAIN = "task-editor";

const TASK_EDITOR_PUBLISHED_LIST_ENDPOINT = "published/list";
const TASK_EDITOR_PUBLISHED_SAVE_ENDPOINT = "published/save";
const TASK_EDITOR_PUBLISHED_REMOVE_ENDPOINT = "published/remove";
const TASK_EDITOR_WORKING_DRAFT_LOAD_ENDPOINT = "draft/load";
const TASK_EDITOR_WORKING_DRAFT_SAVE_ENDPOINT = "draft/save";
const TASK_EDITOR_DRAFT_LIST_ENDPOINT = "draft/list";
const TASK_EDITOR_DRAFT_REMOVE_ENDPOINT = "draft/remove";
const TASK_EDITOR_AUTH_HEADER_KEY = "X-Auth-Token";
const TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR =
  "taskEditorApi requires user access token.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

function unwrapResponsePayload<T>(
  response: CustomResponseFormat<T> | undefined
): unknown {
  if (!response) {
    return undefined;
  }

  const result = response.result;
  if (isRecord(result) && "data" in result) {
    return (result as Record<string, unknown>).data;
  }
  if (result !== undefined) {
    return result;
  }
  return response.data;
}

function normalizeTaskEditorTimestamp(value: unknown) {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const epochSecond = value.epochSecond;
  const nano = value.nano;

  if (typeof epochSecond !== "number" || !Number.isFinite(epochSecond)) {
    return undefined;
  }

  const nanoToMillis =
    typeof nano === "number" && Number.isFinite(nano)
      ? Math.floor(nano / 1_000_000)
      : 0;

  return new Date(epochSecond * 1000 + nanoToMillis).toISOString();
}

function normalizeTaskEditorUserId(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.floor(value);
  }

  if (typeof value === "string") {
    const normalizedValue = Number(value.trim());

    if (Number.isFinite(normalizedValue)) {
      return Math.floor(normalizedValue);
    }
  }

  return undefined;
}

function extractTaskEditorPayload(payload: unknown): unknown {
  if (!isRecord(payload)) {
    return payload;
  }

  if (isRecord(payload.task)) {
    return {
      ...payload.task,
      ...(typeof payload.taskId === "string" ? { taskId: payload.taskId } : {}),
    };
  }

  if (isRecord(payload.draft)) {
    return payload.draft;
  }

  return payload;
}

function normalizeTaskEditorStatePayload(payload: unknown): TaskEditorState | undefined {
  if (!isRecord(payload)) {
    return undefined;
  }

  const normalizedBlocks = (Array.isArray(payload.blocks) ? payload.blocks : [])
    .map((block, blockIndex) => {
      if (!isRecord(block)) {
        return undefined;
      }

      const normalizedType =
        block.type === "Header1"
          ? "Header1"
          : block.type === "LastCard"
            ? "LastCard"
            : "Instruction";
      const blockId =
        typeof block.id === "string"
          ? block.id
          : `task-editor-block-server-${blockIndex}`;
      const normalizedLines = (
        Array.isArray(block.lines) ? block.lines : []
      ).map((line, lineIndex) => {
        if (!isRecord(line)) {
          return undefined;
        }

        const normalizedLineType =
          line.type === "ButtonGroup"
            ? "ButtonGroup"
            : line.type === "ButtonSelection"
              ? "ButtonSelection"
            : line.type === "Quiz"
              ? "Quiz"
            : line.type === "Textarea"
              ? "Textarea"
              : line.type === "Image"
                ? "Image"
                : "Typography";
        const lineId =
          typeof line.id === "string" ? line.id : `${blockId}-line-${lineIndex}`;
        const normalizedLine: Record<string, unknown> = {
          id: lineId,
          type: normalizedLineType,
          content: typeof line.content === "string" ? line.content : "",
        };

        if (normalizedLineType === "Typography") {
          normalizedLine.typographyStyle =
            line.typographyStyle === "Label" ||
            line.typographyStyle === "Alert" ||
            line.typographyStyle === "Dimmed"
              ? line.typographyStyle
              : "Body";
        }

        if (normalizedLineType === "Textarea") {
          normalizedLine.textareaPlaceholder =
            typeof line.textareaPlaceholder === "string"
              ? line.textareaPlaceholder
              : "";
        }

        if (
          normalizedLineType === "ButtonGroup" ||
          normalizedLineType === "ButtonSelection" ||
          normalizedLineType === "Quiz"
        ) {
          normalizedLine.options = (
            Array.isArray(line.options) ? line.options : []
          ).map((option) => {
            if (!isRecord(option)) {
              return {
                text: typeof option === "string" ? option : "",
              };
            }

            return {
              text:
                typeof option.text === "string"
                  ? option.text
                  : typeof option.translationKey === "string"
                    ? option.translationKey
                    : "",
              ...(typeof option.showFromId === "string"
                ? { showFromId: option.showFromId }
                : {}),
              ...(typeof option.showToId === "string"
                ? { showToId: option.showToId }
                : {}),
              ...(typeof option.restartFromId === "string"
                ? { restartFromId: option.restartFromId }
                : {}),
              ...(typeof option.hideFromId === "string"
                ? { hideFromId: option.hideFromId }
                : {}),
              ...(option.isCorrect === true ? { isCorrect: true } : {}),
            };
          });
        }

        if (normalizedLineType === "Quiz") {
          normalizedLine.incorrectMessage =
            typeof line.incorrectMessage === "string"
              ? line.incorrectMessage
              : "";
        }

        if (normalizedLineType === "ButtonSelection") {
          normalizedLine.allowMultipleSelection =
            line.allowMultipleSelection !== false;
        }

        return normalizedLine;
      }).filter(isDefined);

      return {
        id: blockId,
        type: normalizedType,
        content: typeof block.content === "string" ? block.content : "",
        ...(normalizedType === "Instruction"
          ? {
              lines: normalizedLines,
            }
          : {}),
      };
    })
    .filter(isDefined);

  return {
    taskId: typeof payload.taskId === "string" ? payload.taskId : "",
    title: typeof payload.title === "string" ? payload.title : "",
    setNumber:
      typeof payload.setNumber === "number" && Number.isFinite(payload.setNumber)
        ? payload.setNumber
        : undefined,
    isKeyActivity: payload.isKeyActivity === true,
    blocks: normalizedBlocks as TaskEditorState["blocks"],
  };
}

function normalizeTaskDraftMapPayload(payload: unknown): Record<string, TaskEditorState> {
  const appendDraftState = (
    draftsByTaskId: Record<string, TaskEditorState>,
    draftTaskId: string,
    draftPayload: unknown
  ) => {
    const normalizedDraftTaskId = draftTaskId.trim();
    if (!normalizedDraftTaskId) {
      return draftsByTaskId;
    }

    const normalizedDraftPayload = normalizeTaskEditorStatePayload(
      extractTaskEditorPayload(draftPayload)
    );

    if (!normalizedDraftPayload) {
      return draftsByTaskId;
    }

    return {
      ...draftsByTaskId,
      [normalizedDraftTaskId]: normalizeTaskEditorState({
        ...normalizedDraftPayload,
        taskId: normalizedDraftTaskId,
      }),
    };
  };

  if (Array.isArray(payload)) {
    return payload.reduce<Record<string, TaskEditorState>>(
      (draftsByTaskId, item) => {
        if (!isRecord(item)) {
          return draftsByTaskId;
        }

        if (typeof item.taskId === "string" && isRecord(item.draft)) {
          return appendDraftState(draftsByTaskId, item.taskId, item.draft);
        }

        if (typeof item.taskId === "string") {
          return appendDraftState(draftsByTaskId, item.taskId, item);
        }

        return draftsByTaskId;
      },
      {}
    );
  }

  if (!isRecord(payload)) {
    return {};
  }

  return Object.entries(payload).reduce<Record<string, TaskEditorState>>(
    (draftsByTaskId, [taskId, draftPayload]) =>
      appendDraftState(draftsByTaskId, taskId, draftPayload),
    {}
  );
}

function normalizePublishedTaskPayload(payload: unknown) {
  const normalizedLegacyTask = normalizePublishedTaskEditorTask(payload);
  if (normalizedLegacyTask) {
    return normalizedLegacyTask;
  }

  const rawTaskPayload = extractTaskEditorPayload(payload);
  const normalizedStatePayload = normalizeTaskEditorStatePayload(rawTaskPayload);

  if (!normalizedStatePayload) {
    return undefined;
  }

  const envelope = isRecord(payload) ? payload : undefined;
  const updatedAt =
    normalizeTaskEditorTimestamp(envelope?.updatedAt) ??
    (isRecord(rawTaskPayload)
      ? normalizeTaskEditorTimestamp(rawTaskPayload.updatedAt)
      : undefined) ??
    new Date().toISOString();
  const versionId =
    (typeof envelope?.versionId === "string" ? envelope.versionId : undefined) ??
    (isRecord(rawTaskPayload) && typeof rawTaskPayload.versionId === "string"
      ? rawTaskPayload.versionId
      : updatedAt);
  const updatedByUserId =
    normalizeTaskEditorUserId(envelope?.updatedByUserId) ??
    normalizeTaskEditorUserId(envelope?.userId) ??
    (isRecord(rawTaskPayload)
      ? normalizeTaskEditorUserId(rawTaskPayload.updatedByUserId) ??
        normalizeTaskEditorUserId(rawTaskPayload.userId)
      : undefined);

  return {
    ...normalizeTaskEditorState(normalizedStatePayload),
    updatedAt,
    versionId,
    ...(updatedByUserId !== undefined ? { updatedByUserId } : {}),
  };
}

function normalizePublishedTaskListPayload(
  payload: unknown
): PublishedTaskEditorTask[] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((task) => normalizePublishedTaskPayload(task))
    .filter((task): task is PublishedTaskEditorTask => Boolean(task));
}

function getTaskEditorAccessTokenFromStorage() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return undefined;
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    if (
      isRecord(parsedUser) &&
      typeof parsedUser.accessToken === "string" &&
      parsedUser.accessToken.trim().length > 0
    ) {
      return parsedUser.accessToken;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function getTaskEditorAccessTokenFromAxiosDefaults() {
  const headerToken =
    axiosInstance.defaults.headers?.[TASK_EDITOR_AUTH_HEADER_KEY];

  return typeof headerToken === "string" && headerToken.trim().length > 0
    ? headerToken
    : undefined;
}

function requireTaskEditorAccessToken() {
  const accessToken =
    getTaskEditorAccessTokenFromStorage() ??
    getTaskEditorAccessTokenFromAxiosDefaults();

  if (!accessToken) {
    throw new Error(TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR);
  }

  return accessToken;
}

function getTaskEditorAuthConfig(config?: {
  params?: Record<string, unknown>;
  headers?: Record<string, unknown>;
}) {
  const accessToken = requireTaskEditorAccessToken();

  return {
    ...config,
    headers: {
      ...(config?.headers ?? {}),
      [TASK_EDITOR_AUTH_HEADER_KEY]: accessToken,
    },
  };
}

export const taskEditorApi = {
  getPublishedTasks: async (): Promise<PublishedTaskEditorTask[]> => {
    const response = await axiosInstance.get<CustomResponseFormat<unknown>>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_PUBLISHED_LIST_ENDPOINT}`,
      getTaskEditorAuthConfig()
    );

    return normalizePublishedTaskListPayload(unwrapResponsePayload(response));
  },

  savePublishedTask: async (
    state: TaskEditorState,
    currentTaskId?: string
  ): Promise<PublishedTaskEditorTask | undefined> => {
    const response = await axiosInstance.post<CustomResponseFormat<unknown>>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_PUBLISHED_SAVE_ENDPOINT}`,
      {
        task: state,
      },
      getTaskEditorAuthConfig({
        params: {
          ...(currentTaskId ? { currentTaskId } : {}),
        },
      })
    );

    return normalizePublishedTaskPayload(unwrapResponsePayload(response));
  },

  removePublishedTask: async (taskId: string) => {
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_PUBLISHED_REMOVE_ENDPOINT}`,
      undefined,
      getTaskEditorAuthConfig({
        params: { taskId },
      })
    );
  },

  getWorkingDraft: async (): Promise<TaskEditorState | undefined> => {
    const response = await axiosInstance.get<CustomResponseFormat<unknown>>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_WORKING_DRAFT_LOAD_ENDPOINT}`,
      getTaskEditorAuthConfig()
    );
    const payload = unwrapResponsePayload(response);

    if (isTaskEditorState(payload)) {
      return normalizeTaskEditorState(payload);
    }

    const normalizedPayload = normalizeTaskEditorStatePayload(
      extractTaskEditorPayload(payload)
    );

    return normalizedPayload
      ? normalizeTaskEditorState(normalizedPayload)
      : undefined;
  },

  saveWorkingDraft: async (state: TaskEditorState) => {
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_WORKING_DRAFT_SAVE_ENDPOINT}`,
      {
        draft: state,
      },
      getTaskEditorAuthConfig()
    );
  },

  getTaskDraftsByTaskId: async (): Promise<Record<string, TaskEditorState>> => {
    const authConfig = getTaskEditorAuthConfig();

    try {
      const response = await axiosInstance.get<CustomResponseFormat<unknown>>(
        `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_DRAFT_LIST_ENDPOINT}`,
        authConfig
      );

      return normalizeTaskDraftMapPayload(unwrapResponsePayload(response));
    } catch {
      return {};
    }
  },

  saveTaskDraft: async (taskId: string, draft: TaskEditorState) => {
    const normalizedTaskId = taskId.trim();

    if (!normalizedTaskId) {
      return;
    }

    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_WORKING_DRAFT_SAVE_ENDPOINT}`,
      {
        taskId: normalizedTaskId,
        draft: {
          ...draft,
          taskId: normalizedTaskId,
        },
      },
      getTaskEditorAuthConfig()
    );
  },

  removeTaskDraft: async (taskId: string) => {
    const normalizedTaskId = taskId.trim();

    if (!normalizedTaskId) {
      return;
    }

    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_DRAFT_REMOVE_ENDPOINT}`,
      undefined,
      getTaskEditorAuthConfig({
        params: {
          taskId: normalizedTaskId,
        },
      })
    );
  },
};
